import { useEffect, useMemo, useState } from "react";
import { Link } from "@tanstack/react-router";
import { ApiError } from "@/api/client";
import {
  useConnections,
  useCreateEvalSuite,
  useCompareEvalRuns,
  useCreateStarterSuite,
  useDefaultModel,
  useDeleteEvalCase,
  useDeleteEvalSuite,
  useEvalGraders,
  useEvalRun,
  useEvalRuns,
  useEvalStatus,
  useEvalSuites,
  useSetEvalRegression,
  useModels,
  usePublishEvalRun,
  useRelationships,
  useSaveEvalCase,
  useStartEvalRun,
  useStopEvalRun,
  type EvalCase,
  type EvalResult,
  type EvalRun,
  type EvalSuite,
} from "@/api/queries";
import { ModelPicker } from "@/components/chat/ModelPicker";
import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader } from "@/components/ui/Card";
import { Chip, type Tone } from "@/components/ui/Chip";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { EmptyState } from "@/components/ui/EmptyState";
import { Field } from "@/components/ui/Field";
import { Modal } from "@/components/ui/Modal";
import { Select, Toggle } from "@/components/ui/Select";
import { Spinner } from "@/components/ui/Spinner";
import { Textarea } from "@/components/ui/Textarea";
import { GaugeIcon, PencilIcon, PlusIcon, SparkIcon, StopIcon, TrashIcon } from "@/components/icons";
import { cn } from "@/lib/cn";

/**
 * The eval harness (EVAL-1): graded questions, run against a model, pinned to
 * the prompt that answered them.
 *
 * Runs spend model tokens — one answer per case — so a run only ever starts
 * from the explicit Run button, with the case count beside it. The ladder
 * fills in from the first poll, and a refresh mid-run picks up where it was:
 * the run lives on the server, not in this page.
 *
 * Grades are mechanical facts about each answer, never string equality over
 * its prose. `fail` is for facts (no query ran, a write reached the
 * database); `warn` is for judgement (no chart, retyped rows). How the answer
 * was parsed — native, parsed, repaired, salvaged — is shown beside the grade
 * as a fact, never held against it.
 */

const STATUS_TONE: Record<string, Tone> = {
  pass: "success",
  warn: "warning",
  fail: "error",
  skipped: "neutral",
  pending: "neutral",
  running: "accent",
};

type ExpectationRow = { grader: string; severity: "fail" | "warn"; arg: string };

/** The one argument a grader takes, edited as text. */
const ARG: Record<string, string | undefined> = {
  has_chart: "family",
  has_artifact: "type",
  artifact_count_at_least: "count",
  answer_overlaps: "terms",
};

function toRows(expectations: Array<Record<string, unknown>>): ExpectationRow[] {
  return expectations.map((entry) => {
    const grader = String(entry.grader ?? "");
    const key = ARG[grader];
    const raw = key ? entry[key] : undefined;
    return {
      grader,
      severity: entry.severity === "warn" ? "warn" : "fail",
      arg: Array.isArray(raw) ? raw.join(", ") : raw === undefined ? "" : String(raw),
    };
  });
}

function fromRows(rows: ExpectationRow[]): Array<Record<string, unknown>> {
  return rows
    .filter((row) => row.grader)
    .map((row) => {
      const key = ARG[row.grader];
      const out: Record<string, unknown> = { grader: row.grader, severity: row.severity };
      if (key && row.arg.trim()) {
        out[key] =
          key === "terms"
            ? row.arg.split(",").map((term) => term.trim()).filter(Boolean)
            : key === "count"
              ? Number(row.arg)
              : row.arg.trim();
      }
      return out;
    });
}

function CaseDialog({
  suite,
  editing,
  onClose,
}: {
  suite: EvalSuite;
  editing: EvalCase | "new" | null;
  onClose: () => void;
}) {
  const { data: graders } = useEvalGraders();
  const save = useSaveEvalCase(suite.id);
  const current = editing && editing !== "new" ? editing : null;
  const [title, setTitle] = useState(current?.title ?? "");
  const [question, setQuestion] = useState(current?.question ?? "");
  const [follows, setFollows] = useState(current?.follows ?? "");
  const [overlay, setOverlay] = useState(current?.dictionary_overlay ?? "");
  const [rows, setRows] = useState<ExpectationRow[]>(
    current
      ? toRows(current.expectations)
      : [
          { grader: "answered", severity: "fail", arg: "" },
          { grader: "ran_successful_query", severity: "fail", arg: "" },
        ],
  );
  const error = save.error instanceof ApiError ? save.error.message : null;

  return (
    <Modal
      open={Boolean(editing)}
      onClose={onClose}
      wide
      title={current ? "Edit case" : "New case"}
      description="Expectations are shape-facts about the answer — they stay true as the data moves."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={save.isPending}
            disabled={!question.trim()}
            onClick={() =>
              save.mutate(
                {
                  id: current?.id,
                  body: {
                    title,
                    question,
                    follows: follows || null,
                    dictionary_overlay: overlay,
                    expectations: fromRows(rows),
                  },
                },
                { onSuccess: onClose },
              )
            }
          >
            Save case
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Field label="Title" value={title} onChange={(event) => setTitle(event.target.value)} />
        <Textarea
          label="Question"
          rows={3}
          value={question}
          onChange={(event) => setQuestion(event.target.value)}
        />
        <Select
          label="Follows"
          value={follows}
          onChange={(event) => setFollows(event.target.value)}
          hint="A follow-up runs in that case's thread — the only way it is really a follow-up."
        >
          <option value="">Nothing — a fresh conversation</option>
          {suite.cases
            .filter((entry) => entry.id !== current?.id)
            .map((entry) => (
              <option key={entry.id} value={entry.id}>
                {entry.title || entry.question.slice(0, 60)}
              </option>
            ))}
        </Select>
        <div className="flex flex-col gap-2">
          <p className="type-label text-ink">Expectations</p>
          {rows.map((row, index) => (
            <div key={index} className="flex flex-wrap items-center gap-2">
              <select
                aria-label="Grader"
                value={row.grader}
                onChange={(event) =>
                  setRows(rows.map((r, i) => (i === index ? { ...r, grader: event.target.value } : r)))
                }
                className="control-sm rounded-element bg-card text-ink type-support border-border min-w-48 border px-2"
              >
                {(graders ?? []).map((grader) => (
                  <option key={grader.grader} value={grader.grader} title={grader.description}>
                    {grader.grader}
                  </option>
                ))}
              </select>
              <select
                aria-label="If unmet"
                value={row.severity}
                onChange={(event) =>
                  setRows(
                    rows.map((r, i) =>
                      i === index ? { ...r, severity: event.target.value as "fail" | "warn" } : r,
                    ),
                  )
                }
                className="control-sm rounded-element bg-card text-ink type-support border-border border px-2"
              >
                <option value="fail">fail if unmet</option>
                <option value="warn">warn if unmet</option>
              </select>
              {ARG[row.grader] ? (
                <input
                  aria-label={ARG[row.grader]}
                  placeholder={ARG[row.grader]}
                  value={row.arg}
                  onChange={(event) =>
                    setRows(rows.map((r, i) => (i === index ? { ...r, arg: event.target.value } : r)))
                  }
                  className="control-sm rounded-element bg-card text-ink type-support border-border min-w-40 flex-1 border px-2"
                />
              ) : null}
              <Button
                size="sm"
                variant="ghost"
                icon={<TrashIcon className="size-3.5" />}
                onClick={() => setRows(rows.filter((_, i) => i !== index))}
              >
                Remove
              </Button>
            </div>
          ))}
          <div>
            <Button
              size="sm"
              icon={<PlusIcon className="size-3.5" />}
              onClick={() => setRows([...rows, { grader: "answered", severity: "warn", arg: "" }])}
            >
              Add expectation
            </Button>
          </div>
        </div>
        <Textarea
          label="Dictionary overlay (optional)"
          rows={3}
          value={overlay}
          onChange={(event) => setOverlay(event.target.value)}
          hint="Planted in the administrator notes for this case's run only — how the injection case tests the guard. Never written to the live dictionary."
        />
        {error ? <p className="type-support text-error">{error}</p> : null}
      </div>
    </Modal>
  );
}

function money(run: EvalRun) {
  if (run.cost_total === null || run.cost_total === undefined) return "unpriced";
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: run.currency || "USD",
    maximumFractionDigits: 4,
  }).format(run.cost_total);
}

type StepFact = {
  tool: string;
  connection_id?: string | null;
  query_index?: number | null;
  inputs?: number[];
  outcome: string;
  row_count?: number | null;
};

/**
 * Where each statement of a cross-source answer ran (MSE-4): the source by
 * name, and a staged join as the `qN` it joined. Nothing for a one-source
 * answer, whose every statement ran on the suite's one source.
 */
function StatementSources({ facts }: { facts: Record<string, unknown> }) {
  const sources = (facts.sources as Array<{ id: string; name: string }> | undefined) ?? [];
  if (sources.length < 2) return null;
  const names = new Map(sources.map((source) => [source.id, source.name]));
  const steps = ((facts.steps as StepFact[] | undefined) ?? []).filter(
    (step) => step.tool === "execute_sql" || step.tool === "query_staged_results",
  );
  if (!steps.length) return null;
  return (
    <ul className="flex flex-col gap-0.5">
      {steps.map((step, index) => (
        <li key={index} className="type-caption text-ink-secondary flex gap-2 normal-case">
          <Chip tone={step.outcome === "ok" ? "neutral" : step.outcome === "blocked" ? "error" : "warning"}>
            {step.outcome}
          </Chip>
          <span>
            {step.tool === "query_staged_results"
              ? `${step.query_index ? `q${step.query_index} = ` : ""}${(step.inputs ?? []).map((input) => `q${input}`).join(" ⋈ ") || "staged"} · staged join`
              : `${step.query_index ? `q${step.query_index} · ` : ""}${names.get(step.connection_id ?? "") ?? "unknown source"}`}
            {step.row_count !== null && step.row_count !== undefined ? ` · ${step.row_count} rows` : ""}
          </span>
        </li>
      ))}
    </ul>
  );
}

function ResultRow({ result }: { result: EvalResult }) {
  const [open, setOpen] = useState(false);
  const facts = result.facts as Record<string, unknown>;
  return (
    <li className="px-3 py-2">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        className="flex w-full flex-wrap items-center gap-2 text-start"
        aria-expanded={open}
      >
        <Chip tone={STATUS_TONE[result.status] ?? "neutral"}>
          {result.status === "running" ? (
            <span className="inline-flex items-center gap-1">
              <Spinner /> running
            </span>
          ) : (
            result.status
          )}
        </Chip>
        <span className="type-support text-ink min-w-40 flex-1">{result.title}</span>
        {facts.parse_mode ? (
          <span className="type-caption text-ink-disabled normal-case">
            {String(facts.parse_mode)}
            {facts.latency_ms ? ` · ${Math.round(Number(facts.latency_ms) / 100) / 10}s` : ""}
          </span>
        ) : null}
      </button>
      {result.detail ? (
        <p className="type-caption text-ink-secondary mt-1 normal-case">{result.detail}</p>
      ) : null}
      {open ? (
        <div className="mt-2 flex flex-col gap-2">
          <p className="type-support text-ink-secondary">“{result.question}”</p>
          <ul className="flex flex-col gap-1">
            {result.grades.map((grade, index) => (
              <li key={index} className="type-caption flex items-start gap-2 normal-case">
                <Chip tone={STATUS_TONE[String(grade.status)] ?? "neutral"}>{String(grade.status)}</Chip>
                <span className="text-ink">{String(grade.grader)}</span>
                <span className="text-ink-secondary">{String(grade.detail)}</span>
              </li>
            ))}
          </ul>
          <StatementSources facts={facts} />
          <p className="type-caption text-ink-disabled normal-case">
            {String(facts.model_label ?? "")} · prompt {String(facts.prompt_version ?? "—")}
            {facts.prompt_revision ? ` r${String(facts.prompt_revision)}` : ""} ·{" "}
            {String(facts.prompt_variant ?? "")} · {String(facts.successful_queries ?? 0)} queries ·{" "}
            {String(facts.input_tokens ?? 0)}+{String(facts.output_tokens ?? 0)} tokens
          </p>
          {result.thread_id ? (
            <Link
              to="/chat/$threadId"
              params={{ threadId: result.thread_id }}
              className="type-caption text-accent normal-case hover:underline"
            >
              Open the answer and its timeline
            </Link>
          ) : null}
        </div>
      ) : null}
    </li>
  );
}

const RATE_LABEL: Record<string, string> = {
  query_success: "Ran its queries",
  chart_attach: "Attached charts",
  from_query: "Drew from the query",
  scope_hold: "Held scope",
  guard_refusal: "Planted write refused",
  cross_source_join: "Joined across sources",
  each_source: "Queried every source",
  declared_key: "Joined on a declared key",
  stayed_in_source: "Stayed in each source",
};

/** Only asked of a multi-source suite: hidden when neither run asked them. */
const CROSS_SOURCE_RATES = new Set(["cross_source_join", "each_source", "declared_key", "stayed_in_source"]);

const MOVE_TONE: Record<string, Tone> = {
  improved: "success",
  regressed: "error",
  same: "neutral",
  only_a: "neutral",
  only_b: "neutral",
};

function rate(value: number | null | undefined) {
  return value === null || value === undefined ? "—" : `${Math.round(value * 100)}%`;
}

/**
 * Two runs of one suite, side by side (EVAL-2): the rates the per-tier table
 * in docs/models.md used to claim by hand, now measured, with each case's
 * movement and the reason one click away.
 */
function CompareView({ a, b }: { a: string; b: string }) {
  const { data, isLoading, error } = useCompareEvalRuns(a, b);
  if (isLoading) return <Spinner />;
  if (error instanceof ApiError) return <p className="type-support text-error">{error.message}</p>;
  if (!data) return null;
  const keys = Object.keys(RATE_LABEL).filter(
    (key) =>
      !CROSS_SOURCE_RATES.has(key) ||
      data.a_metrics.rates[key] != null ||
      data.b_metrics.rates[key] != null,
  );
  return (
    <div className="flex flex-col gap-3">
      {data.findings.length ? (
        <ul className="flex flex-col gap-1">
          {data.findings.map((line) => (
            <li key={line} className="type-support text-ink">
              {line}
            </li>
          ))}
        </ul>
      ) : (
        <p className="type-support text-ink-secondary">No rate moved between these runs.</p>
      )}
      {data.caveats.map((line) => (
        <p key={line} className="type-caption text-warning normal-case">
          {line}
        </p>
      ))}
      <div className="overflow-x-auto">
        <table className="type-support w-full border-collapse">
          <thead>
            <tr className="text-ink-secondary text-start">
              <th className="px-2 py-1 text-start font-normal"></th>
              <th className="px-2 py-1 text-start font-normal">A · {data.a_label}</th>
              <th className="px-2 py-1 text-start font-normal">B · {data.b_label}</th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-border border-t">
              <td className="px-2 py-1">Pass / warn / fail</td>
              <td className="px-2 py-1">
                {data.a_metrics.passed} / {data.a_metrics.warned} / {data.a_metrics.failed}
              </td>
              <td className="px-2 py-1">
                {data.b_metrics.passed} / {data.b_metrics.warned} / {data.b_metrics.failed}
              </td>
            </tr>
            {keys.map((key) => (
              <tr key={key} className="border-border border-t">
                <td className="px-2 py-1">{RATE_LABEL[key]}</td>
                <td className="px-2 py-1">{rate(data.a_metrics.rates[key])}</td>
                <td className="px-2 py-1">{rate(data.b_metrics.rates[key])}</td>
              </tr>
            ))}
            <tr className="border-border border-t">
              <td className="px-2 py-1">How answers parsed</td>
              <td className="px-2 py-1">
                {Object.entries(data.a_metrics.contract_paths).map(([k, v]) => `${k} ${v}`).join(" · ")}
              </td>
              <td className="px-2 py-1">
                {Object.entries(data.b_metrics.contract_paths).map(([k, v]) => `${k} ${v}`).join(" · ")}
              </td>
            </tr>
            <tr className="border-border border-t">
              <td className="px-2 py-1">Median latency / tokens</td>
              <td className="px-2 py-1">
                {data.a_metrics.latency_median_ms ?? "—"} ms / {data.a_metrics.tokens_median ?? "—"}
              </td>
              <td className="px-2 py-1">
                {data.b_metrics.latency_median_ms ?? "—"} ms / {data.b_metrics.tokens_median ?? "—"}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <ul className="divide-border border-border rounded-element divide-y border">
        {data.rows.map((row) => (
          <li key={row.case_id} className="flex flex-col gap-1 px-3 py-2">
            <div className="flex flex-wrap items-center gap-2">
              <Chip tone={MOVE_TONE[row.movement] ?? "neutral"}>{row.movement}</Chip>
              <span className="type-support text-ink flex-1">{row.title}</span>
              <span className="type-caption text-ink-disabled normal-case">
                {row.a ?? "—"} → {row.b ?? "—"}
              </span>
            </div>
            {row.movement === "regressed" && row.b_detail ? (
              <p className="type-caption text-error normal-case">{row.b_detail}</p>
            ) : null}
          </li>
        ))}
      </ul>
    </div>
  );
}

function RunView({ runId }: { runId: string }) {
  const { data: run } = useEvalRun(runId);
  const stop = useStopEvalRun();
  const publish = usePublishEvalRun();
  if (!run) return <Spinner />;
  const done = run.results.filter((result) =>
    ["pass", "warn", "fail", "skipped"].includes(result.status),
  ).length;
  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <Chip tone={run.status === "running" ? "accent" : run.status === "complete" ? "success" : "neutral"}>
          {run.status}
        </Chip>
        <span className="type-support text-ink">
          {run.status === "running" ? `${done} of ${run.cases} graded` : `${run.passed} pass · ${run.warned} warn · ${run.failed} fail · ${run.skipped} skipped`}
        </span>
        <span className="flex-1" />
        {run.status === "running" ? (
          <Button
            size="sm"
            icon={<StopIcon className="size-3.5" />}
            loading={stop.isPending}
            onClick={() => stop.mutate(run.id)}
          >
            Stop
          </Button>
        ) : null}
        {run.status === "complete" && run.model_ref ? (
          publish.isSuccess ? (
            <Chip tone="success">
              published to the catalog{run.connection_ids.length > 1 ? " · cross-source" : ""}
            </Chip>
          ) : (
            <Button
              size="sm"
              loading={publish.isPending}
              title={
                run.connection_ids.length > 1
                  ? "Write this run's rates onto the model's cross-source measurement. Its single-source measurement is left as it is."
                  : "Write this run's rates onto the model's catalog row, labelled as a measurement."
              }
              onClick={() => publish.mutate(run.id)}
            >
              Publish to catalog
            </Button>
          )
        ) : null}
      </div>
      <p className="type-caption text-ink-disabled normal-case">
        {run.model_label ?? run.model_ref ?? "deployment default"} · prompt{" "}
        {run.prompt_version ?? "—"}
        {run.prompt_revision ? ` r${run.prompt_revision}` : ""} · {run.prompt_variant ?? ""} · suite
        r{run.suite_revision} · {run.input_tokens + run.output_tokens} tokens · {money(run)}
      </p>
      <ul className="divide-border border-border rounded-element divide-y border">
        {run.results.map((result) => (
          <ResultRow key={result.id} result={result} />
        ))}
      </ul>
    </div>
  );
}

const WEEKDAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

/**
 * The weekly regression (EVAL-3): one suite per deployment, against a model,
 * every week — claimed per window, so a restart never runs it twice — and the
 * operator is told when it passes fewer cases than the week before.
 */
function RegressionCard({ suite }: { suite: EvalSuite }) {
  const { data: models } = useModels();
  const save = useSetEvalRegression(suite.id);
  const parts = (suite.regression_cron ?? "").split(" ");
  const cronDay = Number(parts[4]);
  const [enabled, setEnabled] = useState(Boolean(suite.regression_enabled));
  const [weekday, setWeekday] = useState(
    Number.isNaN(cronDay) || !suite.regression_cron ? 0 : (cronDay + 6) % 7,
  );
  const [hour, setHour] = useState(Number(parts[1]) || 6);
  const [modelRef, setModelRef] = useState(suite.regression_model_ref ?? "");
  return (
    <Card>
      <CardHeader
        title="Weekly regression"
        description="Run this suite every week and be told when it passes fewer cases than the week before. One suite per deployment; each run spends model tokens."
      />
      <CardBody className="flex flex-col gap-3">
        <Toggle label="Run this suite every week" checked={enabled} onChange={setEnabled} />
        <div className="grid gap-3 sm:grid-cols-3">
          <Select label="Day" value={weekday} onChange={(event) => setWeekday(Number(event.target.value))}>
            {WEEKDAYS.map((name, index) => (
              <option key={name} value={index}>
                {name}
              </option>
            ))}
          </Select>
          <Select label="Hour" value={hour} onChange={(event) => setHour(Number(event.target.value))}>
            {Array.from({ length: 24 }, (_, value) => (
              <option key={value} value={value}>
                {String(value).padStart(2, "0")}:00
              </option>
            ))}
          </Select>
          <Select label="Model" value={modelRef} onChange={(event) => setModelRef(event.target.value)}>
            <option value="">The deployment default</option>
            {(models ?? []).map((model) => (
              <option key={model.ref} value={model.ref}>
                {model.label}
              </option>
            ))}
          </Select>
        </div>
        <div>
          <Button
            loading={save.isPending}
            onClick={() =>
              save.mutate({ enabled, weekday, hour, model_ref: modelRef || null })
            }
          >
            Save schedule
          </Button>
        </div>
      </CardBody>
    </Card>
  );
}

function SuiteView({ suite }: { suite: EvalSuite }) {
  const { data: runs } = useEvalRuns(suite.id);
  const { data: models } = useModels();
  const { data: defaultModel } = useDefaultModel();
  const start = useStartEvalRun(suite.id);
  const removeCase = useDeleteEvalCase();
  const removeSuite = useDeleteEvalSuite();
  const [modelRef, setModelRef] = useState("");
  const [selectedRun, setSelectedRun] = useState<string | null>(null);
  const [baseline, setBaseline] = useState<string>("");
  const [editing, setEditing] = useState<EvalCase | "new" | null>(null);
  const [deletingCase, setDeletingCase] = useState<EvalCase | null>(null);
  const [deletingSuite, setDeletingSuite] = useState(false);
  const catalog = useMemo(() => models ?? [], [models]);

  // The deployment default first, never just whatever sorts first: the top
  // of the list may be a paid cloud model, and a run spends one answer per case.
  useEffect(() => {
    if (modelRef) return;
    const preferred = catalog.find((model) => model.ref === defaultModel?.ref) ?? catalog[0];
    if (preferred) setModelRef(preferred.ref);
  }, [catalog, modelRef, defaultModel]);
  useEffect(() => {
    const newest = runs?.[0];
    if (!selectedRun && newest) setSelectedRun(newest.id);
  }, [runs, selectedRun]);

  const running = runs?.some((run) => run.status === "running");

  return (
    <div className="flex flex-col gap-5">
      <Card>
        <CardHeader
          title={suite.name}
          description={`${suite.connection_names.length ? suite.connection_names.join(" + ") : suite.connection_name || "source deleted"} · ${suite.cases.length} cases · revision ${suite.revision}`}
          action={
            <Button size="sm" variant="ghost" icon={<TrashIcon className="size-3.5" />} onClick={() => setDeletingSuite(true)}>
              Delete suite
            </Button>
          }
        />
        <CardBody className="flex flex-col gap-3">
          {suite.multi ? (
            <div className="flex flex-wrap items-center gap-2">
              {suite.connection_names.map((name, index) => (
                <Chip key={`${name}-${index}`} tone={suite.missing_sources.includes(name) ? "error" : "neutral"}>
                  {name}
                  {index === 0 ? " · primary" : ""}
                  {suite.missing_sources.includes(name) ? " · deleted" : ""}
                </Chip>
              ))}
              <span className="type-caption text-ink-disabled normal-case">
                Every case is asked across these sources.
              </span>
            </div>
          ) : null}
          {suite.missing_sources.length ? (
            <p className="bg-error-subtle text-error type-support rounded-element px-3 py-2">
              {suite.missing_sources.join(" and ")} {suite.missing_sources.length === 1 ? "was" : "were"}{" "}
              deleted, so this suite cannot ask its questions as written. Its history is kept below.
            </p>
          ) : null}
          <ul className="divide-border border-border rounded-element divide-y border">
            {suite.cases.map((entry) => (
              <li key={entry.id} className="flex flex-wrap items-start gap-2 px-3 py-2">
                <div className="min-w-48 flex-1">
                  <p className="type-support text-ink">{entry.title || entry.question.slice(0, 80)}</p>
                  <p className="type-caption text-ink-disabled normal-case">
                    {entry.expectations.map((e) => String(e.grader)).join(" · ")}
                    {entry.follows ? " · follow-up" : ""}
                    {entry.dictionary_overlay ? " · overlay planted" : ""}
                    {entry.source !== "hand" ? ` · ${entry.source.split(":")[0]}` : ""}
                  </p>
                </div>
                <Button size="sm" variant="ghost" icon={<PencilIcon className="size-3.5" />} onClick={() => setEditing(entry)}>
                  Edit
                </Button>
                <Button size="sm" variant="ghost" icon={<TrashIcon className="size-3.5" />} onClick={() => setDeletingCase(entry)}>
                  Delete
                </Button>
              </li>
            ))}
          </ul>
          <div>
            <Button size="sm" icon={<PlusIcon className="size-3.5" />} onClick={() => setEditing("new")}>
              Add case
            </Button>
          </div>
        </CardBody>
      </Card>

      <RegressionCard key={`${suite.id}-${suite.regression_cron}`} suite={suite} />

      <Card>
        <CardHeader
          title="Run"
          description="One answer per case, asked of the model you pick. This spends model tokens."
        />
        <CardBody className="flex flex-wrap items-center gap-3">
          {suite.multi ? (
            <p className="type-caption text-ink-disabled w-full normal-case">
              This suite is sent the prompt across sources, so measuring a candidate prompt here
              measures its multi-source templates — an override across sources if it has one, and
              its addendum.
            </p>
          ) : null}
          <ModelPicker models={catalog} value={modelRef} onChange={setModelRef} disabled={running} />
          <Button
            variant="primary"
            icon={<SparkIcon className="size-4" />}
            loading={start.isPending}
            disabled={running || !suite.cases.length || !modelRef || suite.missing_sources.length > 0}
            onClick={() =>
              start.mutate(modelRef || null, { onSuccess: (run) => setSelectedRun(run.id) })
            }
          >
            Run {suite.cases.length} case{suite.cases.length === 1 ? "" : "s"}
          </Button>
        </CardBody>
      </Card>

      <Card>
        <CardHeader title="Runs" description="Each pinned to the model, prompt and suite revision that produced it." />
        <CardBody className="flex flex-col gap-4">
          {runs && runs.length ? (
            <>
              <div className="flex flex-wrap gap-2">
                {runs.map((run) => (
                  <button
                    key={run.id}
                    type="button"
                    onClick={() => setSelectedRun(run.id)}
                    className={cn(
                      "type-caption rounded-element motion-fast border px-2.5 py-1 normal-case",
                      run.id === selectedRun
                        ? "border-accent bg-accent-subtle text-accent"
                        : "border-border text-ink-secondary hover:bg-hover",
                    )}
                  >
                    {new Date(run.started_at).toLocaleString()} · {run.model_label ?? run.model_ref ?? "default"} ·{" "}
                    {run.status === "running" ? "running" : `${run.passed}/${run.cases} pass`}
                  </button>
                ))}
              </div>
              {runs.length > 1 && selectedRun ? (
                <Select
                  label="Compare against"
                  value={baseline}
                  onChange={(event) => setBaseline(event.target.value)}
                  hint="A is the run you pick here; B is the run selected above."
                >
                  <option value="">Don&rsquo;t compare</option>
                  {runs
                    .filter((run) => run.id !== selectedRun && run.status !== "running")
                    .map((run) => (
                      <option key={run.id} value={run.id}>
                        {new Date(run.started_at).toLocaleString()} · {run.model_label ?? run.model_ref ?? "default"}
                        {run.prompt_revision ? ` · prompt r${run.prompt_revision}` : ""}
                      </option>
                    ))}
                </Select>
              ) : null}
              {selectedRun && baseline ? (
                <CompareView a={baseline} b={selectedRun} />
              ) : selectedRun ? (
                <RunView runId={selectedRun} />
              ) : null}
            </>
          ) : (
            <p className="type-support text-ink-secondary">No runs yet.</p>
          )}
        </CardBody>
      </Card>

      {editing ? (
        <CaseDialog
          key={editing === "new" ? "new" : editing.id}
          suite={suite}
          editing={editing}
          onClose={() => setEditing(null)}
        />
      ) : null}
      <ConfirmDialog
        open={Boolean(deletingCase)}
        onClose={() => setDeletingCase(null)}
        loading={removeCase.isPending}
        title="Delete this case?"
        description={<>“{deletingCase?.title || deletingCase?.question}” will not be asked again.</>}
        consequence={<>Past runs keep the answers they graded for it.</>}
        onConfirm={() => {
          if (deletingCase) removeCase.mutate(deletingCase.id, { onSuccess: () => setDeletingCase(null) });
        }}
      />
      <ConfirmDialog
        open={deletingSuite}
        onClose={() => setDeletingSuite(false)}
        loading={removeSuite.isPending}
        title="Delete this suite?"
        description={<>“{suite.name}” will be deleted.</>}
        consequence={<>Its {suite.cases.length} cases and every run&rsquo;s results go with it.</>}
        onConfirm={() => removeSuite.mutate(suite.id, { onSuccess: () => setDeletingSuite(false) })}
      />
    </div>
  );
}

export function EvalsPage() {
  const { data: suites, isLoading } = useEvalSuites();
  const { data: status } = useEvalStatus();
  const { data: connections } = useConnections();
  const starter = useCreateStarterSuite();
  const create = useCreateEvalSuite();
  const [selected, setSelected] = useState<string | null>(null);
  const [source, setSource] = useState("");
  const [also, setAlso] = useState<string[]>([]);
  const [name, setName] = useState("");
  const { data: related } = useRelationships(source || undefined);
  // The primary first, then the others in the order they were ticked.
  const picked = source ? [source, ...also.filter((id) => id !== source)] : [];
  const relatedIds = new Set(
    (related ?? []).flatMap((item) => [item.left.connection_id, item.right.connection_id]),
  );
  const suite = useMemo(
    () => (suites ?? []).find((entry) => entry.id === selected) ?? suites?.[0] ?? null,
    [suites, selected],
  );
  useEffect(() => {
    const first = connections?.[0];
    if (!source && first) setSource(first.id);
  }, [connections, source]);

  return (
    <div className="mx-auto w-full max-w-6xl px-4 py-8 sm:px-8 sm:py-10">
      <header className="mb-8">
        <p className="type-caption text-ink-disabled">Admin</p>
        <h1 className="type-h1 text-ink mt-2">Evals</h1>
        <p className="type-body text-ink-secondary mt-3 max-w-2xl">
          Graded questions you can ask any model, against any prompt revision — so &ldquo;the answers
          got worse when the prompt changed&rdquo; is a measurement rather than a feeling.
        </p>
      </header>

      {status?.regression_drop ? (
        <p className="bg-error-subtle text-error type-support rounded-element mb-5 px-3 py-2">
          The weekly regression “{String((status.regression_drop as Record<string, unknown>).suite_name)}” passed{" "}
          {String((status.regression_drop as Record<string, unknown>).passed)} of{" "}
          {String((status.regression_drop as Record<string, unknown>).cases)} cases, down from{" "}
          {String((status.regression_drop as Record<string, unknown>).previous_passed)}. Compare it
          with the run before it below.
        </p>
      ) : null}

      <div className="grid gap-5 lg:grid-cols-[18rem_1fr]">
        <div className="flex flex-col gap-4">
          <Card>
            <CardHeader title="Suites" />
            <CardBody className="flex flex-col gap-2">
              {isLoading ? (
                <Spinner />
              ) : (suites ?? []).length ? (
                (suites ?? []).map((entry) => (
                  <button
                    key={entry.id}
                    type="button"
                    onClick={() => setSelected(entry.id)}
                    className={cn(
                      "rounded-element motion-fast border px-3 py-2 text-start",
                      entry.id === suite?.id
                        ? "border-accent bg-accent-subtle"
                        : "border-border hover:bg-hover",
                    )}
                  >
                    <p className="type-support text-ink">{entry.name}</p>
                    <p className="type-caption text-ink-disabled normal-case">
                      {entry.cases.length} cases ·{" "}
                      {entry.connection_names.length
                        ? entry.connection_names.join(" + ")
                        : entry.connection_name}
                      {entry.missing_sources.length ? " · source deleted" : ""}
                    </p>
                  </button>
                ))
              ) : (
                <p className="type-support text-ink-secondary">No suites yet.</p>
              )}
            </CardBody>
          </Card>
          <Card>
            <CardHeader title="New suite" />
            <CardBody className="flex flex-col gap-3">
              <Select
                label="Source"
                value={source}
                onChange={(event) => {
                  setSource(event.target.value);
                  setAlso([]);
                }}
              >
                {(connections ?? []).map((connection) => (
                  <option key={connection.id} value={connection.id}>
                    {connection.name}
                  </option>
                ))}
              </Select>
              {(connections ?? []).length > 1 ? (
                <fieldset className="flex flex-col gap-1.5">
                  <legend className="type-label text-ink mb-1">Also ask across</legend>
                  {(connections ?? [])
                    .filter((connection) => connection.id !== source)
                    .sort((x, y) => Number(relatedIds.has(y.id)) - Number(relatedIds.has(x.id)))
                    .map((connection) => (
                      <Toggle
                        key={connection.id}
                        label={`${connection.name}${relatedIds.has(connection.id) ? " · related" : ""}`}
                        checked={also.includes(connection.id)}
                        onChange={(checked) =>
                          setAlso((current) =>
                            checked ? [...current, connection.id] : current.filter((id) => id !== connection.id),
                          )
                        }
                      />
                    ))}
                </fieldset>
              ) : null}
              <Button
                variant="primary"
                icon={<GaugeIcon className="size-4" />}
                loading={starter.isPending}
                disabled={!source}
                onClick={() => starter.mutate(picked, { onSuccess: (made) => setSelected(made.id) })}
              >
                Starter suite
              </Button>
              <p className="type-caption text-ink-disabled normal-case">
                {picked.length > 1
                  ? "A question across these sources and its follow-up, the injection probe, and a single-source question — all asked across them."
                  : "The health check’s graded questions, the injection probe, and the M3 chart case."}
              </p>
              <Field label="Or an empty one, named" value={name} onChange={(event) => setName(event.target.value)} />
              <Button
                disabled={!source || !name.trim()}
                loading={create.isPending}
                onClick={() =>
                  create.mutate(
                    { name, connection_ids: picked },
                    {
                      onSuccess: (made) => {
                        setName("");
                        setSelected(made.id);
                      },
                    },
                  )
                }
              >
                Create
              </Button>
            </CardBody>
          </Card>
        </div>
        {suite ? (
          <SuiteView key={suite.id} suite={suite} />
        ) : (
          <EmptyState
            icon={<GaugeIcon className="size-8" />}
            title="No suite selected"
            detail="Make a starter suite for a source, then run it."
          />
        )}
      </div>
    </div>
  );
}
